import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { filledSheets, readWorkbook } from "@/office/excel/workbook";
import { KEIN_INHALT, nichtPruefbar } from "../schnittstelle";

export const lektionAnsicht: Lesson = {
  id: "ansicht",
  title: "Die Ansicht bändigen",
  summary: "Fixierte Kopfzeilen und farbige Register – zwei Handgriffe, die jede große Tabelle lesbar halten.",
  durationMinutes: 8,
  learningGoals: [
    "Die Kopfzeile beim Scrollen sichtbar halten",
    "Blätter über Registerfarben gruppieren",
    "Den Unterschied zwischen Ausblenden und Löschen kennen",
  ],
  steps: [
    {
      id: "fenster-fixieren",
      title: "Kopfzeile fixieren",
      explanation: [
        "Ab der dreißigsten Zeile beginnt das Ratespiel: In welcher Spalte stand noch " +
          "einmal der Preis? Man scrollt hoch, schaut nach, scrollt zurück – und hat es " +
          "beim nächsten Mal wieder vergessen. Dagegen gibt es genau einen Handgriff.",
        "„Ansicht“ → „Fenster fixieren“ friert alles oberhalb und links der markierten " +
          "Zelle ein. Der Trick liegt darin, vorher die richtige Zelle zu markieren: " +
          "Steht der Cursor in A2, bleibt Zeile 1 stehen. Steht er in B2, bleiben Zeile " +
          "1 und Spalte A stehen – praktisch, wenn links die Bezeichnungen stehen.",
        "Fixieren ist reine Ansicht. Es ändert nichts an den Daten, wird aber in der " +
          "Datei gespeichert und gilt damit auch für alle, die die Datei später öffnen. " +
          "Es ist damit eine der wenigen Einstellungen, mit denen man anderen die Arbeit " +
          "erleichtert, ohne etwas zu riskieren.",
      ],
      keyPoints: [
        "Ansicht → Fenster fixieren friert alles ober- und linksseitig der Markierung ein.",
        "Cursor in A2 fixiert die Kopfzeile, Cursor in B2 zusätzlich die erste Spalte.",
        "Die Fixierung wird mitgespeichert und gilt auch für andere Leser.",
      ],
      task: {
        instruction:
          "Fixiere auf dem Blatt mit deiner Liste die Kopfzeile, sodass sie beim " +
          "Scrollen stehen bleibt.",
        hint: "Zelle A2 markieren, dann Ansicht → Fenster fixieren → Fenster fixieren.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.view) {
            return nichtPruefbar(
              "Der fixierte Bereich eines Blattes",
              "1.7",
              "Scrolle nach unten und sieh nach, ob die Kopfzeile stehen bleibt.",
            );
          }

          const fixiert = mappe.sheets.filter((blatt) => blatt.frozenRange !== null);
          if (fixiert.length === 0) {
            return fail(
              "Auf keinem Blatt ist bisher etwas fixiert.",
              "Markiere Zelle A2 und wähle Ansicht → Fenster fixieren. Wichtig ist die Zelle: " +
                "Von ihr aus fixiert Excel nach oben und nach links.",
            );
          }

          const namen = fixiert.map((blatt) => `„${blatt.name}“ (${blatt.frozenRange})`).join(", ");
          return pass(`Fixiert auf ${namen} – die Kopfzeile bleibt beim Scrollen stehen.`);
        },
      },
    },
    {
      id: "registerfarben",
      title: "Registerfarben als Ordnungssystem",
      explanation: [
        "Sobald eine Arbeitsmappe mehr als eine Handvoll Blätter hat, wird die " +
          "Registerleiste unübersichtlich. Farben schaffen hier Ordnung, wenn sie einer " +
          "Regel folgen statt der Laune: alle Rohdatenblätter grau, alle " +
          "Auswertungsblätter blau, das Einstellungsblatt gelb.",
        "Die Farbe setzt du per Rechtsklick auf das Register unter " +
          "„Registerfarbe“. Ein Detail, das oft irritiert: Das aktive Blatt zeigt seine " +
          "Farbe nur als schmalen Streifen unter dem Namen – erst wenn du auf ein " +
          "anderes Blatt wechselst, ist das Register vollflächig eingefärbt.",
        "Farbe ersetzt keinen guten Namen, sie ergänzt ihn. Wer sich allein auf Farben " +
          "verlässt, schließt außerdem alle aus, die Farben schlecht unterscheiden " +
          "können – rund acht Prozent der Männer. Der Name muss also für sich stehen.",
      ],
      keyPoints: [
        "Rechtsklick aufs Register → Registerfarbe.",
        "Farben nach einer Regel vergeben, nicht nach Laune.",
        "Die Farbe des aktiven Blattes erscheint nur als schmaler Streifen.",
      ],
      task: {
        instruction: "Gib mindestens einem Blattregister eine Farbe.",
        hint: "Rechtsklick auf das Register → Registerfarbe → eine Farbe wählen.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.view) {
            return nichtPruefbar(
              "Die Registerfarbe eines Blattes",
              "1.7",
              "Sieh unten in der Registerleiste nach, ob die Farbe sichtbar ist – wechsle dafür auf ein anderes Blatt.",
            );
          }

          // Ohne gesetzte Farbe meldet Excel eine leere Zeichenkette.
          const farbig = mappe.sheets.filter(
            (blatt) => blatt.tabColor !== null && blatt.tabColor.trim().length > 0,
          );

          if (farbig.length === 0) {
            return fail(
              "Bisher trägt kein Blattregister eine Farbe.",
              "Rechtsklick auf ein Register, dann „Registerfarbe“. Denk daran: Beim aktiven Blatt " +
                "siehst du die Farbe nur als schmalen Streifen unter dem Namen.",
            );
          }

          return pass(
            `${farbig.length === 1 ? "Ein Register trägt" : `${farbig.length} Register tragen`} jetzt eine Farbe: ` +
              `${farbig.map((blatt) => `„${blatt.name}“`).join(", ")}.`,
          );
        },
      },
    },
    {
      id: "ausblenden",
      title: "Ausblenden statt löschen",
      explanation: [
        "Zeilen, Spalten und ganze Blätter lassen sich ausblenden statt löschen. Der " +
          "Unterschied ist wichtig: Ausgeblendete Zellen sind unsichtbar, aber weiterhin " +
          "da – Formeln, die darauf verweisen, rechnen ganz normal weiter, und eine " +
          "SUMME zählt sie mit.",
        "Genau das ist Segen und Falle zugleich. Segen, weil sich Hilfsspalten mit " +
          "Zwischenrechnungen so aus dem Weg räumen lassen, ohne die Rechnung zu " +
          "zerstören. Falle, weil eine ausgeblendete Zeile mit einem Ausreißer eine " +
          "Auswertung verfälschen kann, ohne dass jemand sie sieht.",
        "Erkennbar sind ausgeblendete Bereiche an der Nummerierung: Springt der " +
          "Zeilenkopf von 12 auf 17, fehlen dazwischen vier Zeilen. Zum Einblenden " +
          "markierst du die beiden Zeilen darum herum und wählst per Rechtsklick " +
          "„Einblenden“. Für ein ausgeblendetes Blatt gilt dasselbe über einen " +
          "Rechtsklick auf ein beliebiges Register.",
      ],
      keyPoints: [
        "Ausgeblendete Zellen rechnen weiter mit – im Gegensatz zu gefilterten.",
        "Strg + 9 blendet Zeilen aus, Strg + 8 Spalten (je nach Tastaturbelegung).",
        "Eine Lücke in der Zeilen- oder Spaltennummerierung verrät ausgeblendete Bereiche.",
      ],
      task: {
        instruction:
          "Sorge dafür, dass in deiner Arbeitsmappe nichts unbeabsichtigt ausgeblendet " +
          "ist: Alle Tabellenblätter sollen sichtbar sein.",
        hint: "Rechtsklick auf ein Register → „Einblenden“. Ist der Eintrag ausgegraut, ist nichts ausgeblendet.",
        check: async () => {
          const mappe = await readWorkbook();
          const versteckt = mappe.sheets.filter(
            (blatt) => blatt.visibility !== null && blatt.visibility !== "Visible",
          );
          const sichtbar = mappe.sheets.filter((blatt) => blatt.visibility === "Visible");

          if (filledSheets(mappe).length === 0 && mappe.features.cells) {
            return fail(KEIN_INHALT, "Trage zuerst deine Liste ein.");
          }

          return fromCriteria(
            [
              criterion(
                versteckt.length === 0
                  ? "Kein Blatt ist ausgeblendet"
                  : `Ausgeblendet: ${versteckt.map((blatt) => `„${blatt.name}“`).join(", ")}`,
                versteckt.length === 0,
              ),
              criterion(
                `Mindestens zwei sichtbare Blätter (gefunden: ${sichtbar.length || mappe.sheets.length})`,
                (sichtbar.length || mappe.sheets.length) >= 2,
              ),
            ],
            {
              success: "Alle Blätter sind sichtbar – nichts versteckt sich vor dir oder anderen.",
              failure: "Da ist noch etwas ausgeblendet.",
              hint: "Rechtsklick auf ein beliebiges Register, dann „Einblenden“ und das Blatt auswählen.",
            },
          );
        },
      },
    },
  ],
};
